import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { RefObject } from "react"
import type { inferRouterOutputs } from "@trpc/server"
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  CircleDashed,
  FileSearch,
  Search,
  ShieldCheck,
  X,
} from "lucide-react"
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryState } from "nuqs"
import { useHotkeys } from "react-hotkeys-hook"

import { Badge, Button, cn, Input, ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@aix/ui"

import type { AppRouter } from "../../../aixinternal-api/src/router/_app"
import { trpc } from "../lib/trpc"
import { healthBadgeVariant, liveUrlFor, operationBadgeVariant, stateBadgeVariant } from "./seoBoardHelpers"

type RouterOutputs = inferRouterOutputs<AppRouter>
type QueueRow = RouterOutputs["seo"]["cockpit"]["queue"]["rows"][number]

const STAGES = [
  "attention",
  "draft",
  "evidence",
  "media",
  "validation",
  "review",
  "ready",
  "active",
  "indexed",
  "performing",
  "pruned",
  "all",
] as const

type QueueStage = (typeof STAGES)[number]
type SeoTab = "operations" | "pages" | "keywords" | "publish" | "scoreboard"

const STAGE_LABELS: Record<QueueStage, string> = {
  attention: "Needs attention",
  draft: "Draft",
  evidence: "Evidence",
  media: "Media",
  validation: "QA failed",
  review: "Human review",
  ready: "Ready",
  active: "Published record",
  indexed: "Indexed",
  performing: "Performing",
  pruned: "Pruned",
  all: "Everything",
}

const PIPELINE_STAGES = STAGES.filter(
  (stage): stage is Exclude<QueueStage, "attention" | "all"> => stage !== "attention" && stage !== "all",
)
const QUEUE_PAGE_SIZE = 75

interface SEOCockpitTabProps {
  productKey: string
  productDomain: string | null | undefined
  onOpenTab: (tab: SeoTab, slug?: string, mode?: "inspect" | "preview" | "edit") => void
}

export default function SEOCockpitTab({ productKey, productDomain, onOpenTab }: SEOCockpitTabProps) {
  const [stage, setStage] = useQueryState("stage", parseAsStringLiteral(STAGES).withDefault("attention"))
  const [selectedItemId, setSelectedItemId] = useQueryState("item", parseAsString.withDefault(""))
  const [search, setSearch] = useQueryState("q", parseAsString.withDefault(""))
  const [queuePage, setQueuePage] = useQueryState("queuePage", parseAsInteger.withDefault(1))
  const [pendingPageEdge, setPendingPageEdge] = useState<"first" | "last" | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const selectedRowRef = useRef<HTMLButtonElement>(null)

  const overview = trpc.seo.cockpit.overview.useQuery({ productKey }, { refetchInterval: 60_000 })
  const audit = trpc.seo.pages.audit.useQuery({ productKey }, { refetchInterval: 60_000 })
  const queue = trpc.seo.cockpit.queue.useQuery({
    productKey,
    stage,
    search: search || undefined,
    limit: QUEUE_PAGE_SIZE,
    offset: (queuePage - 1) * QUEUE_PAGE_SIZE,
  })
  const rows = useMemo(() => queue.data?.rows ?? [], [queue.data?.rows])
  const selectedIndex = rows.findIndex((row) => row.id === selectedItemId)
  const selected = selectedIndex >= 0 ? rows[selectedIndex] : null

  const selectItem = useCallback((id: string) => void setSelectedItemId(id, { history: "push" }), [setSelectedItemId])
  const selectStage = (nextStage: QueueStage) => {
    setPendingPageEdge(null)
    void setStage(nextStage, { history: "push" })
    void setSelectedItemId(null)
    void setQueuePage(null)
  }

  useEffect(() => {
    if (queue.isLoading || rows.length === 0) return
    if (pendingPageEdge) {
      const nextRow = pendingPageEdge === "first" ? rows[0] : rows.at(-1)
      if (nextRow) selectItem(nextRow.id)
      setPendingPageEdge(null)
      return
    }
    if (!selected) selectItem(rows[0].id)
  }, [pendingPageEdge, queue.isLoading, rows, selectItem, selected])

  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: "nearest" })
  }, [selectedItemId])

  const moveSelection = (direction: 1 | -1) => {
    if (rows.length === 0) return
    if (direction === 1 && selectedIndex === rows.length - 1 && queue.data?.hasMore) {
      setPendingPageEdge("first")
      void setQueuePage(queuePage + 1, { history: "push" })
      void setSelectedItemId(null)
      return
    }
    if (direction === -1 && selectedIndex <= 0 && queuePage > 1) {
      setPendingPageEdge("last")
      void setQueuePage(queuePage - 1, { history: "push" })
      void setSelectedItemId(null)
      return
    }
    const nextIndex = selectedIndex < 0 ? 0 : Math.max(0, Math.min(rows.length - 1, selectedIndex + direction))
    selectItem(rows[nextIndex].id)
  }

  useHotkeys("j, down", () => moveSelection(1), { preventDefault: true, enableOnFormTags: false }, [
    queue.data?.hasMore,
    queuePage,
    selectedIndex,
    rows,
  ])
  useHotkeys("k, up", () => moveSelection(-1), { preventDefault: true, enableOnFormTags: false }, [
    queue.data?.hasMore,
    queuePage,
    selectedIndex,
    rows,
  ])
  useHotkeys("g", () => rows[0] && selectItem(rows[0].id), { preventDefault: true, enableOnFormTags: false }, [rows])
  useHotkeys(
    "shift+g",
    () => {
      const lastRow = rows.at(-1)
      if (lastRow) selectItem(lastRow.id)
    },
    { preventDefault: true, enableOnFormTags: false },
    [rows],
  )
  useHotkeys("/", () => searchRef.current?.focus(), { preventDefault: true, enableOnFormTags: false })
  useHotkeys(
    "enter",
    () => {
      if (selected === null) return
      if (selected.entityType === "page") onOpenTab("pages", selected.slug ?? undefined, "inspect")
      else onOpenTab("operations")
    },
    { preventDefault: true, enableOnFormTags: false },
    [selected, onOpenTab],
  )
  useHotkeys(
    "e",
    () => {
      if (selected?.entityType === "page") onOpenTab("pages", selected.slug ?? undefined, "edit")
    },
    { preventDefault: true, enableOnFormTags: false },
    [selected, onOpenTab],
  )
  useHotkeys(
    "p",
    () => {
      if (selected?.entityType === "page") onOpenTab("pages", selected.slug ?? undefined, "preview")
    },
    { preventDefault: true, enableOnFormTags: false },
    [selected, onOpenTab],
  )
  useHotkeys(
    "a",
    () => {
      if (selected?.waitingOn === "human" || selected?.entityType === "release") onOpenTab("operations")
    },
    { preventDefault: true, enableOnFormTags: false },
    [selected, onOpenTab],
  )
  useHotkeys("backslash", () => {
    void setSearch(null)
    selectStage("attention")
  })
  useHotkeys(
    "esc",
    () => {
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
    },
    { enableOnFormTags: true },
  )

  return (
    <div className="flex h-full flex-col bg-background">
      <CockpitSummary
        overview={overview.data}
        audit={audit.data}
        loading={overview.isLoading}
        auditLoading={audit.isLoading}
        auditError={audit.error?.message}
        error={overview.error?.message ?? audit.error?.message}
        stage={stage}
        onStageChange={selectStage}
      />

      <div className="flex items-center gap-2 border-b border-border bg-background px-4 py-2">
        <div className="relative min-w-0 flex-1 md:max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchRef}
            value={search}
            onChange={(event) => {
              setPendingPageEdge(null)
              void setSearch(event.target.value || null)
              void setQueuePage(null)
            }}
            aria-label="Search SEO pages, runs, releases, or blockers"
            placeholder="Search pages, runs, releases, or blockers…"
            className="pl-9 pr-9"
          />
          {search ? (
            <button
              type="button"
              onClick={() => void setSearch(null)}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        <span className="hidden text-xs tabular-nums text-muted-foreground sm:inline">
          {queue.isLoading
            ? "Loading…"
            : `${Math.min((queuePage - 1) * QUEUE_PAGE_SIZE + 1, queue.data?.total ?? 0)}–${Math.min(
                queuePage * QUEUE_PAGE_SIZE,
                queue.data?.total ?? 0,
              )} of ${queue.data?.total ?? 0}`}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={queuePage <= 1}
          onClick={() => {
            setPendingPageEdge("last")
            void setQueuePage(Math.max(1, queuePage - 1), { history: "push" })
            void setSelectedItemId(null)
          }}
        >
          Previous
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!queue.data?.hasMore}
          onClick={() => {
            setPendingPageEdge("first")
            void setQueuePage(queuePage + 1, { history: "push" })
            void setSelectedItemId(null)
          }}
        >
          Next
        </Button>
      </div>

      <ResizablePanelGroup
        direction="horizontal"
        autoSaveId="seo-cockpit-panes"
        className="!h-auto flex-1 overflow-hidden"
      >
        <ResizablePanel defaultSize={58} minSize={34}>
          <QueueList
            rows={rows}
            selectedItemId={selectedItemId}
            selectedRowRef={selectedRowRef}
            loading={queue.isLoading}
            error={queue.error?.message}
            onSelect={selectItem}
            onClear={() => {
              void setSearch(null)
              selectStage("attention")
            }}
          />
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={42} minSize={30} maxSize={62}>
          <QueueInspector row={selected} productKey={productKey} productDomain={productDomain} onOpenTab={onOpenTab} />
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}

function CockpitSummary({
  overview,
  audit,
  loading,
  auditLoading,
  auditError,
  error,
  stage,
  onStageChange,
}: {
  overview: RouterOutputs["seo"]["cockpit"]["overview"] | undefined
  audit: RouterOutputs["seo"]["pages"]["audit"] | undefined
  loading: boolean
  auditLoading: boolean
  auditError: string | undefined
  error: string | undefined
  stage: QueueStage
  onStageChange: (stage: QueueStage) => void
}) {
  const reviewCount =
    (overview?.stages.review ?? 0) + (overview?.approvals.runs ?? 0) + (overview?.approvals.releases ?? 0)
  const stageCount = (pipelineStage: (typeof PIPELINE_STAGES)[number]): number => {
    if (pipelineStage === "review") return reviewCount
    return overview?.stages[pipelineStage] ?? 0
  }

  return (
    <section className="space-y-3 border-b border-border bg-muted/25 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Operator cockpit</p>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="font-heading text-xl font-semibold tracking-tight">
              {overview?.product.name ?? "SEO pipeline"}
            </h2>
            <span className="text-xs text-muted-foreground">
              {loading ? "Loading pipeline…" : `${overview?.pageCount ?? 0} managed pages`}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-right text-xs">
          <Metric label="Impressions" value={overview?.performance.impressions ?? 0} />
          <Metric label="Clicks" value={overview?.performance.clicks ?? 0} />
          <Metric label="Signups" value={overview?.performance.signups ?? 0} />
        </div>
      </div>

      {error ? (
        <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
          {error}
        </div>
      ) : null}

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5" aria-label="SEO launch readiness">
        <LaunchSignal
          label="Staging"
          value={overview?.operations.activeStagingRelease ? "Active" : "Not active"}
          detail={overview?.operations.activeStagingRelease?.id ?? "No staging release"}
          tone={overview?.operations.activeStagingRelease ? "good" : "warn"}
        />
        <LaunchSignal
          label="Production"
          value={overview?.operations.activeRelease ? "Active" : "Not active"}
          detail={overview?.operations.activeRelease?.id ?? "No production release"}
          tone={overview?.operations.activeRelease ? "good" : "warn"}
        />
        <LaunchSignal
          label="Inventory"
          value={`${overview?.inventory.gatedPages ?? 0} gated · ${overview?.inventory.publishedPages ?? 0} published`}
          detail={`${overview?.inventory.managedPages ?? 0} managed total`}
          tone={
            (overview?.inventory.gatedPages ?? 0) > 0 || (overview?.inventory.publishedPages ?? 0) > 0 ? "good" : "warn"
          }
        />
        <LaunchSignal
          label="Technical graph"
          value={auditLoading ? "Checking…" : auditError ? "Unavailable" : `${audit?.issueCount ?? 0} issue(s)`}
          detail={
            auditLoading
              ? "Technical audit in progress"
              : auditError
                ? auditError
                : `${audit?.orphans.length ?? 0} orphan · ${audit?.brokenLinks.length ?? 0} broken`
          }
          tone={auditLoading ? "warn" : auditError || (audit?.issueCount ?? 0) > 0 ? "bad" : "good"}
        />
        <LaunchSignal
          label="Measurement"
          value={`${overview?.performance.metricPages ?? 0}/${overview?.inventory.managedPages ?? 0} observed`}
          detail={
            overview?.performance.freshness === "never"
              ? "No managed-page observation"
              : `${overview?.performance.freshness ?? "unknown"} · ${overview?.performance.observationAgeDays ?? 0}d old`
          }
          tone={overview?.performance.freshness === "fresh" ? "good" : "warn"}
        />
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1" aria-label="SEO pipeline stages">
        {PIPELINE_STAGES.map((pipelineStage, index) => {
          const count = stageCount(pipelineStage)
          return (
            <button
              type="button"
              key={pipelineStage}
              onClick={() => onStageChange(pipelineStage)}
              className={cn(
                "group relative min-w-[104px] flex-1 rounded-md border px-3 py-2 text-left transition-colors",
                stage === pipelineStage
                  ? "border-primary bg-primary text-primary-foreground shadow-sm"
                  : "border-border bg-card hover:border-primary/50 hover:bg-muted",
              )}
            >
              <span className="block text-[11px] font-medium uppercase tracking-wide opacity-75">
                {String(index + 1).padStart(2, "0")} · {STAGE_LABELS[pipelineStage]}
              </span>
              <span className="mt-1 block text-xl font-semibold tabular-nums">{count.toLocaleString()}</span>
            </button>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={stage === "attention" ? "default" : "outline"}
          onClick={() => onStageChange("attention")}
        >
          <AlertTriangle className="mr-1.5 h-4 w-4" /> Needs attention
        </Button>
        <Button size="sm" variant={stage === "all" ? "default" : "outline"} onClick={() => onStageChange("all")}>
          Everything
        </Button>
        {overview?.topBlockers.slice(0, 3).map((blocker) => (
          <Badge key={blocker.ruleId} variant="outline" className="font-mono text-[11px]">
            {blocker.ruleId} · {blocker.count}
          </Badge>
        ))}
        <span className="ml-auto hidden text-xs text-muted-foreground lg:inline">
          Oldest managed observation {overview?.performance.observedAt ?? "never"}
        </span>
      </div>
    </section>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <span className="block text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-foreground">{value.toLocaleString()}</span>
    </div>
  )
}

function LaunchSignal({
  label,
  value,
  detail,
  tone,
}: {
  label: string
  value: string
  detail: string
  tone: "good" | "warn" | "bad"
}) {
  const toneClass =
    tone === "good"
      ? "border-success/40 bg-success/5"
      : tone === "bad"
        ? "border-destructive/40 bg-destructive/5"
        : "border-border bg-card"
  return (
    <div className={cn("min-w-0 rounded-md border px-3 py-2", toneClass)}>
      <span className="block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="mt-0.5 block truncate text-sm font-semibold">{value}</span>
      <span className="block truncate text-[11px] text-muted-foreground" title={detail}>
        {detail}
      </span>
    </div>
  )
}

function QueueList({
  rows,
  selectedItemId,
  selectedRowRef,
  loading,
  error,
  onSelect,
  onClear,
}: {
  rows: QueueRow[]
  selectedItemId: string
  selectedRowRef: RefObject<HTMLButtonElement>
  loading: boolean
  error: string | undefined
  onSelect: (id: string) => void
  onClear: () => void
}) {
  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="h-20 animate-pulse rounded-md border border-border bg-muted/50" />
        ))}
      </div>
    )
  }
  if (error) return <StateMessage icon={<AlertTriangle />} title="Queue unavailable" detail={error} />
  if (rows.length === 0) {
    return (
      <StateMessage
        icon={<CheckCircle2 />}
        title="Nothing in this queue"
        detail="Try another stage or clear the current search."
        action={<Button onClick={onClear}>Show needs attention</Button>}
      />
    )
  }

  return (
    <div className="h-full overflow-y-auto bg-background p-3">
      <div className="space-y-1.5">
        {rows.map((row) => {
          const selected = row.id === selectedItemId
          return (
            <button
              ref={selected ? selectedRowRef : undefined}
              type="button"
              key={row.id}
              onClick={() => onSelect(row.id)}
              className={cn(
                "w-full rounded-md border p-3 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                selected ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-card hover:bg-muted/60",
              )}
            >
              <div className="flex items-start gap-3">
                <EntityIcon type={row.entityType} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="truncate text-sm font-medium text-foreground">{row.title}</p>
                    <span className="text-[11px] text-muted-foreground">{relativeDate(row.updatedAt)}</span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{row.subtitle || row.entityType}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <StageBadge stage={row.stage} />
                    <Badge variant={operationBadgeVariant(row.status)} className="text-[10px]">
                      {row.status}
                    </Badge>
                    {row.blockerCount > 0 ? (
                      <span className="text-[11px] text-destructive">{row.blockerCount} blocker(s)</span>
                    ) : null}
                    <span className="text-[11px] text-muted-foreground">waiting on {row.waitingOn}</span>
                    {row.lastCheckedAt ? (
                      <span className="text-[11px] text-muted-foreground">
                        checked {relativeDate(row.lastCheckedAt)}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function QueueInspector({
  row,
  productKey,
  productDomain,
  onOpenTab,
}: {
  row: QueueRow | null
  productKey: string
  productDomain: string | null | undefined
  onOpenTab: (tab: SeoTab, slug?: string, mode?: "inspect" | "preview" | "edit") => void
}) {
  const pageQuery = trpc.seo.pages.get.useQuery(
    { productKey, slug: row?.slug ?? "" },
    { enabled: row?.entityType === "page" && Boolean(row.slug) },
  )
  const metricQuery = trpc.seo.metrics.page.useQuery(
    { productKey, slug: row?.slug ?? "" },
    { enabled: row?.entityType === "page" && Boolean(row.slug) },
  )
  const deliveryQuery = trpc.seo.pages.delivery.useQuery(
    { productKey, slug: row?.slug ?? "" },
    { enabled: row?.entityType === "page" && Boolean(row.slug) },
  )

  if (!row) {
    return (
      <StateMessage
        icon={<FileSearch />}
        title="Select an item"
        detail="Choose a page, run, or release to understand why it is waiting and what to do next."
      />
    )
  }

  const page = row.entityType === "page" ? pageQuery.data : null
  const findings = page?.gateResult?.findings ?? []
  const openFindings = findings.filter((finding) => finding.repairStatus !== "fixed")
  const liveUrl =
    deliveryQuery.data?.production.available && row.slug && productDomain ? liveUrlFor(productDomain, row.slug) : null

  return (
    <aside className="h-full overflow-y-auto bg-card">
      <div className="sticky top-0 z-10 border-b border-border bg-card/95 px-5 py-4 backdrop-blur">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <StageBadge stage={row.stage} />
              <Badge variant={operationBadgeVariant(row.status)}>{row.status}</Badge>
            </div>
            <h3 className="break-words font-heading text-lg font-semibold tracking-tight">{row.title}</h3>
            <p className="mt-1 text-xs text-muted-foreground">{row.subtitle}</p>
          </div>
          {liveUrl ? (
            <Button size="icon" variant="outline" asChild>
              <a href={liveUrl} target="_blank" rel="noreferrer" aria-label="Open live page">
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </Button>
          ) : null}
        </div>
      </div>

      <div className="space-y-5 p-5">
        <InspectorSection title="Why it is here" icon={<AlertTriangle className="h-4 w-4" />}>
          <p className="text-sm leading-relaxed text-foreground">{row.reason ?? "No blocker reason was recorded."}</p>
        </InspectorSection>

        <InspectorSection title="Recommended next action" icon={<Activity className="h-4 w-4" />}>
          <p className="text-sm leading-relaxed text-foreground">{row.nextAction}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {row.entityType === "page" ? (
              <>
                <Button size="sm" onClick={() => onOpenTab("pages", row.slug ?? undefined, "inspect")}>
                  Open page workspace
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onOpenTab("pages", row.slug ?? undefined, "preview")}
                >
                  Preview current draft
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => onOpenTab("operations")}>
                Open operation controls
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onOpenTab("scoreboard")}>
              Performance
            </Button>
          </div>
        </InspectorSection>

        {row.entityType === "page" ? (
          pageQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading page contract…</p>
          ) : page ? (
            <>
              <InspectorSection title="Publish readiness" icon={<ShieldCheck className="h-4 w-4" />}>
                <div className="grid grid-cols-3 gap-2">
                  <InspectorStat label="Evidence" value={page.content.evidence?.length ?? 0} />
                  <InspectorStat label="Media" value={mediaCount(page.content)} />
                  <InspectorStat label="Links" value={page.content.internalLinks.length} />
                </div>
              </InspectorSection>

              <InspectorSection title="Lifecycle visibility" icon={<Activity className="h-4 w-4" />}>
                <PageLifecycle
                  page={page}
                  metric={metricQuery.data}
                  metricLoading={metricQuery.isLoading}
                  metricError={metricQuery.error?.message}
                  delivery={deliveryQuery.data}
                  deliveryLoading={deliveryQuery.isLoading}
                  deliveryError={deliveryQuery.error?.message}
                />
              </InspectorSection>

              <InspectorSection
                title={`Open findings (${openFindings.length})`}
                icon={<CircleDashed className="h-4 w-4" />}
              >
                {openFindings.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No open structured findings.</p>
                ) : (
                  <div className="space-y-2">
                    {openFindings.map((finding, index) => (
                      <div
                        key={`${finding.ruleId}-${index}`}
                        className="rounded-md border border-border bg-background p-3"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <code className="text-[11px] text-muted-foreground">{finding.ruleId}</code>
                          <Badge variant={finding.severity === "error" ? "destructive" : "secondary"}>
                            {finding.severity}
                          </Badge>
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed">{finding.message}</p>
                        {finding.autoFixable ? <p className="mt-1 text-xs text-primary">Agent-fixable</p> : null}
                      </div>
                    ))}
                  </div>
                )}
              </InspectorSection>

              <InspectorSection title="Page contract" icon={<FileSearch className="h-4 w-4" />}>
                <dl className="space-y-2 text-sm">
                  <DetailRow
                    label="Template"
                    value={`${page.templateKey} · ${page.content.templateVersion ?? "unversioned"}`}
                  />
                  <DetailRow label="Keyword" value={page.content.keyword} />
                  <DetailRow label="Cluster" value={page.cluster ?? "Unassigned"} />
                  <DetailRow
                    label="State"
                    value={page.state}
                    badge={<Badge variant={stateBadgeVariant(page.state)}>{page.state}</Badge>}
                  />
                  <DetailRow
                    label="Health"
                    value={page.health}
                    badge={<Badge variant={healthBadgeVariant(page.health)}>{page.health}</Badge>}
                  />
                </dl>
              </InspectorSection>
            </>
          ) : (
            <p className="text-sm text-destructive">The selected page could not be loaded.</p>
          )
        ) : (
          <InspectorSection title="Operator context" icon={<FileSearch className="h-4 w-4" />}>
            <dl className="space-y-2 text-sm">
              <DetailRow label="Entity" value={row.entityType} />
              <DetailRow label="Identifier" value={row.entityId} />
              <DetailRow label="Updated" value={new Date(row.updatedAt).toLocaleString()} />
            </dl>
          </InspectorSection>
        )}
      </div>
    </aside>
  )
}

function PageLifecycle({
  page,
  metric,
  metricLoading,
  metricError,
  delivery,
  deliveryLoading,
  deliveryError,
}: {
  page: RouterOutputs["seo"]["pages"]["get"]
  metric: RouterOutputs["seo"]["metrics"]["page"] | undefined
  metricLoading: boolean
  metricError: string | undefined
  delivery: RouterOutputs["seo"]["pages"]["delivery"] | undefined
  deliveryLoading: boolean
  deliveryError: string | undefined
}) {
  const metricRow = metric?.metric
  const unavailableDetail = metricLoading ? "Loading…" : metricError ? "Unavailable" : "Not measured"
  const released = delivery?.staging.available === true || delivery?.production.available === true
  const releaseDetail = deliveryLoading
    ? "Checking…"
    : deliveryError
      ? "Unavailable"
      : delivery?.production.available
        ? `Production · ${delivery.production.releaseId}`
        : delivery?.staging.available
          ? `Staging · ${delivery.staging.releaseId}`
          : "Not in an active release"
  const steps = [
    { label: "Keyword", complete: Boolean(page.content.keyword), detail: page.content.keyword || "Missing" },
    {
      label: "Evidence",
      complete: (page.content.evidence?.length ?? 0) > 0,
      detail: `${page.content.evidence?.length ?? 0} source(s)`,
    },
    { label: "Content", complete: Boolean(page.content.h1), detail: page.templateKey },
    { label: "Gate", complete: page.gateResult?.passed === true, detail: page.gateResult?.passed ? "Passed" : "Open" },
    {
      label: "Release",
      complete: released,
      detail: releaseDetail,
    },
    {
      label: "Live",
      complete: delivery?.production.available === true,
      detail: deliveryLoading ? "Checking…" : deliveryError ? "Unavailable" : (delivery?.production.reason ?? "Live"),
    },
    {
      label: "Search",
      complete: metricRow?.indexed === 1,
      detail: metricRow?.indexationStatus ?? (metricRow ? "Observed, not indexed" : unavailableDetail),
    },
    {
      label: "Conversion",
      complete: (metricRow?.signups ?? 0) > 0,
      detail: metricRow ? `${metricRow.signups} signup(s)` : unavailableDetail,
    },
  ]

  return (
    <div className="space-y-2">
      {metricLoading || metricError || deliveryLoading || deliveryError ? (
        <p className={cn("text-xs", metricError || deliveryError ? "text-destructive" : "text-muted-foreground")}>
          {deliveryError
            ? `Delivery unavailable: ${deliveryError}`
            : metricError
              ? `Measurement unavailable: ${metricError}`
              : "Loading delivery and measurement state…"}
        </p>
      ) : null}
      <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {steps.map((step, index) => {
          return (
            <li
              key={step.label}
              className={cn(
                "rounded-md border p-2",
                step.complete ? "border-success/40 bg-success/5" : "border-border bg-background",
              )}
            >
              <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                {index + 1}. {step.label}
              </span>
              <span className="mt-0.5 block truncate text-xs font-medium" title={step.detail}>
                {step.complete ? "Complete" : step.detail}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function InspectorSection({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section>
      <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {icon} {title}
      </h4>
      {children}
    </section>
  )
}

function InspectorStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <span className="block text-[11px] text-muted-foreground">{label}</span>
      <span className="mt-1 block text-lg font-semibold tabular-nums">{value}</span>
    </div>
  )
}

function DetailRow({ label, value, badge }: { label: string; value: string; badge?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 pb-2 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-right font-medium">{badge ?? value}</dd>
    </div>
  )
}

function StageBadge({ stage }: { stage: QueueRow["stage"] }) {
  const urgent = stage === "evidence" || stage === "media" || stage === "validation"
  const positive = stage === "ready" || stage === "indexed" || stage === "performing"
  let variant: "destructive" | "success" | "secondary" = "secondary"
  if (urgent) variant = "destructive"
  if (positive) variant = "success"
  return <Badge variant={variant}>{STAGE_LABELS[stage]}</Badge>
}

function EntityIcon({ type }: { type: QueueRow["entityType"] }) {
  const icon = type === "page" ? <FileSearch /> : type === "run" ? <Activity /> : <ShieldCheck />
  return (
    <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">
      {icon}
    </span>
  )
}

function StateMessage({
  icon,
  title,
  detail,
  action,
}: {
  icon: React.ReactNode
  title: string
  detail: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex h-full items-center justify-center p-8 text-center">
      <div className="max-w-sm">
        <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground [&>svg]:h-5 [&>svg]:w-5">
          {icon}
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold">{title}</h3>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{detail}</p>
        {action ? <div className="mt-4">{action}</div> : null}
      </div>
    </div>
  )
}

function mediaCount(content: RouterOutputs["seo"]["pages"]["get"]["content"]): number {
  return (
    (content.heroImage ? 1 : 0) +
    (content.media?.length ?? 0) +
    (content.beforeAfterMedia ? 2 : 0) +
    (content.examples ?? []).reduce((count, example) => count + (example.media?.length ?? 0), 0)
  )
}

function relativeDate(value: Date | string): string {
  const diffMs = Date.now() - new Date(value).getTime()
  const minutes = Math.max(0, Math.floor(diffMs / 60_000))
  if (minutes < 1) return "now"
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}
